/**
 * Müşteriler state: the GET /customers read model, loaded when the screen opens and read
 * again on Tekrar dene. Read only; nothing here decides anything.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { CustomersView } from '../api/customers.js';
import { useStore } from './context.js';

export interface CustomersState {
  view: CustomersView | null;
  error: string | null;
  loading: boolean;
}

export class CustomersStore {
  private state: CustomersState = { view: null, error: null, loading: false };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): CustomersState => this.state;

  private set(patch: Partial<CustomersState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Loads once; the screen calls it on mount. */
  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getCustomers();
        this.set({ view, error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }
}

export function useCustomers(): { store: CustomersStore; state: CustomersState } {
  const store = useStore().customers;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
